'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import dynamic from 'next/dynamic';

import { Button, Flex, Stack } from '@backyard-ui/core';
import { useMediaQuery } from '@backyard-ui/hooks';
import {
  ChevronUp,
  ChevronDown,
  PlayCircle,
  SpinnerCubeOutline,
} from '@backyard-ui/icons';
import { mediasCarouselTracker as tracker } from '@leroy-merlin-br/backpack-analytics';
import useEmblaCarousel from 'embla-carousel-react';

import { type Product } from '@/infra/services/catalog/product/get-product-info/model';
import { algolia } from '@/infra/tracking/algolia';
import * as wishlistTracker from '@/infra/tracking/tracker/wishlist/tracker';
import { buildLightboxSlides } from '@/presenters/components/common/LightboxGallery/LightboxGallery.utils';
import { Wishlist } from '@/presenters/components/common/Wishlist';
import { useProductStore } from '@/presenters/store/catalog/product';

import { MainItem } from './MainItem';
import {
  mainCarouselStyles,
  navigatorCarouselStyles,
  arrowStyles,
} from './MediasCarousel.styles';
import { NavigatorItem } from './NavigatorItem';
import { buildVisualMedias } from './utils';
import { Sketch3D, Video } from './utils/build-visual-medias';
import { mapToWishlistProduct } from './utils/map-to-wishlist-product';
import { WishlistButton } from './WishlistButton';

const LightboxGallery = dynamic(
  () =>
    import('@/presenters/components/common/LightboxGallery').then(
      (mod) => mod.LightboxGallery
    ),
  {
    ssr: false,
  }
);

interface MediasCarouselProps extends Pick<Product, 'visualMedias'> {}

function MediasCarousel(props: Readonly<MediasCarouselProps>) {
  const { visualMedias } = props;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [slidesInView, setSlidesInView] = useState<number[]>([]);
  const [isLightBoxOpen, setIsLightBoxOpen] = useState(false);

  const isDesktop = useMediaQuery('md');
  const hasLightboxBeenImported = useRef(false);

  const product = useProductStore((state) => state.product);
  const currentSeller = useProductStore((state) => state.currentSeller);

  const { _id: id } = product;

  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({ loop: true });
  const [emblaNavigatorRef, emblaNavigatorApi] = useEmblaCarousel({
    dragFree: true,
    axis: isDesktop ? 'y' : 'x',
    loop: true,
  });

  const handleSlidesInView = useCallback(() => {
    if (emblaMainApi) {
      setSlidesInView((slidesInView) => {
        if (slidesInView.length === emblaMainApi.slideNodes().length) {
          emblaMainApi.off('slidesInView', handleSlidesInView);
        }

        const inView = emblaMainApi
          .slidesInView()
          .filter((index) => !slidesInView.includes(index));

        return slidesInView.concat(inView);
      });
    }
  }, [emblaMainApi]);

  const handleEmblaMainSelect = useCallback(() => {
    if (emblaMainApi) {
      const selectedIndex = emblaMainApi.selectedScrollSnap();

      setSelectedIndex(selectedIndex);
      emblaNavigatorApi?.scrollTo(selectedIndex);
    }
  }, [emblaMainApi, emblaNavigatorApi]);

  useEffect(() => {
    emblaMainApi?.on('slidesInView', handleSlidesInView);
    emblaMainApi?.on('select', handleEmblaMainSelect);

    return () => {
      emblaMainApi?.off('slidesInView', handleSlidesInView);
      emblaMainApi?.off('select', handleEmblaMainSelect);
    };
  }, [emblaMainApi, handleEmblaMainSelect, handleSlidesInView]);

  const medias = buildVisualMedias(visualMedias);
  const mainCarouselClassNames = mainCarouselStyles();
  const navigatorCarouselClassNames = navigatorCarouselStyles();
  const arrowClassNames = arrowStyles();
  const hasVideo = medias.some((media) => media instanceof Video);
  const hasSketch3D = medias.some((media) => media instanceof Sketch3D);
  const shouldRenderButtons = hasVideo || hasSketch3D;

  const handle3DButtonClick = useCallback(() => {
    const index = medias.findIndex((media) => media instanceof Sketch3D);

    if (index !== -1) {
      emblaMainApi?.scrollTo(index);
    }
  }, [emblaMainApi, medias]);

  const handleVideoButtonClick = useCallback(() => {
    const index = medias.findIndex((media) => media instanceof Video);

    if (index !== -1) {
      emblaMainApi?.scrollTo(index);
    }
  }, [emblaMainApi, medias]);

  const handleKeyDown = (event: {
    key: string;
    preventDefault: () => void;
  }) => {
    if (!emblaMainApi || !emblaNavigatorApi) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      emblaMainApi.scrollPrev();
      algolia.sendPdpClickEvent();
      tracker.pushClickPrev({ productId: id });
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      emblaMainApi.scrollNext();
      algolia.sendPdpClickEvent();
      tracker.pushClickNext({ productId: id });
    }
  };

  return (
    <Stack direction={{ initial: 'column-reverse', md: 'row' }} spacing="4">
      <Flex
        direction={{ initial: 'row', md: 'column' }}
        className="md:h-[528px]"
        justify="center"
        align="center"
      >
        {isDesktop && (
          <button
            aria-label="Slide anterior"
            className={arrowClassNames}
            onClick={() => {
              emblaMainApi?.scrollPrev();
              algolia.sendPdpClickEvent();
              tracker.pushClickPrev({ productId: id });
            }}
            data-testid="Carousel-control-prev"
            onKeyDown={handleKeyDown}
          >
            <ChevronUp />
          </button>
        )}

        <div
          className={navigatorCarouselClassNames.viewport()}
          ref={emblaNavigatorRef}
          role="button"
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <div className={navigatorCarouselClassNames.container()}>
            {medias.map((media, i) => (
              <NavigatorItem
                data-testid={`thumb-${i}`}
                key={i}
                media={media}
                onClick={() => emblaMainApi?.scrollTo(i)}
                isActive={selectedIndex === i}
              />
            ))}
          </div>
        </div>

        {isDesktop && (
          <button
            aria-label="Próximo Slide"
            className={arrowClassNames}
            onClick={() => {
              emblaMainApi?.scrollNext();
              algolia.sendPdpClickEvent();
              tracker.pushClickNext({ productId: id });
            }}
            data-testid="Carousel-control-next"
            onKeyDown={handleKeyDown}
          >
            <ChevronDown />
          </button>
        )}
      </Flex>

      <Stack direction="column" spacing="6" className="w-full">
        <div>
          <Wishlist
            item={{ id, type: 'products' }}
            trigger={WishlistButton}
            onClick={() => {
              algolia.sendPdpClickEvent();
              tracker.pushClickWishlistButton({ productId: id });
            }}
            onCreate={(listName) => {
              wishlistTracker.createWishlist(listName);
              wishlistTracker.addProductToWishlist({
                product: mapToWishlistProduct({ product, currentSeller }),
                listName,
              });
            }}
            onAdd={(listName) =>
              wishlistTracker.addProductToWishlist({
                product: mapToWishlistProduct({ product, currentSeller }),
                listName,
              })
            }
            onRemove={() =>
              wishlistTracker.removeProductFromWishlist(
                mapToWishlistProduct({ product, currentSeller })
              )
            }
          />
        </div>

        <div
          className={mainCarouselClassNames.viewport()}
          ref={emblaMainRef}
          role="button"
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <div className={mainCarouselClassNames.container()}>
            {medias.map((media, i) => (
              <MainItem
                data-testid={`item-${i}`}
                key={i}
                index={i}
                isInView={slidesInView.indexOf(i) > -1}
                media={media}
                isActive={selectedIndex === i}
                onClick={() => {
                  hasLightboxBeenImported.current = true;
                  setIsLightBoxOpen(true);
                }}
              />
            ))}
          </div>
        </div>

        {hasLightboxBeenImported && (
          <LightboxGallery
            isOpen={isLightBoxOpen}
            onClose={() => setIsLightBoxOpen(false)}
            medias={buildLightboxSlides(visualMedias.medias)}
            selectedIndex={selectedIndex}
            onViewItem={({ index }) => {
              setSelectedIndex(index);
              emblaMainApi?.scrollTo(index);
            }}
          />
        )}

        {shouldRenderButtons && (
          <Stack spacing="2">
            {hasVideo && (
              <Button
                size="sm"
                variant="outline"
                iconLeft={<PlayCircle />}
                onClick={() => {
                  handleVideoButtonClick();
                  algolia.sendPdpClickEvent();
                  tracker.pushClickVideoButton({ productId: id });
                }}
              >
                Vídeo
              </Button>
            )}

            {hasSketch3D && (
              <Button
                size="sm"
                variant="outline"
                iconLeft={<SpinnerCubeOutline className="h-6 w-6" />}
                onClick={() => {
                  handle3DButtonClick();
                  algolia.sendPdpClickEvent();
                  tracker.pushClick3dButton({ productId: id });
                }}
              >
                3D
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export default MediasCarousel;
